/**
 * Component: Product Installation component
 * ------------------------------------------------------------------------------
 * A JavaScript component to enable the bundle functionality for product installation on tagged products.
 * Order of functionality:
 * - Parent product page (includes installation) functionality.
 * - Child product page (excludes installation) functionality.
 * - General functionality.
 *
 * @namespace productInstallation
 */
import axios from 'axios'
import {formatMoney} from '@shopify/theme-currency'

import {on} from '../helpers/utils'
import cssClasses from '../helpers/cssClasses'

class ProductInstallation {
    constructor (theme, elem) {
        this._theme = theme
        this._elem = elem
        this._settings = {
            installationParent: '[js-installation="parent"]',
            installationDetails: '[js-installation="details"]',
            installationContainer: '[js-installation="container"]',
            installationError: '[js-installation="error"]',
            installationSoldOut: '[js-installation="soldOut"]',
            installationInput: '[js-installation="input"]',
            installationCost: '[js-installation="cost"]',

            productPrice: '[js-product-form="price"]',
            addToCartButton: '[js-ajax-cart="addToCart"]',

            addToCartContainer: '[js-product-form="addToCartContainer"]',
            quantitySelector: '[js-product-form="quantitySelector"]',
            bisForm: '[js-product-form="bis"]',
            klarnaWidget: '[js-product-klarna="container"]',
            ...JSON.parse(elem.getAttribute('data-product-installation') || '{}')
        }

        /**
         * DOM node selectors.
         */
        this._nodeSelectors = {
            installationParent: document.querySelector(this._settings.installationParent),
            installationDetails: document.querySelector(this._settings.installationDetails),
            installationContainer: document.querySelector(this._settings.installationContainer),
            installationError: document.querySelector(this._settings.installationError),
            installationSoldOut: document.querySelector(this._settings.installationSoldOut),
            installationInput: document.querySelector(this._settings.installationInput),
            installationCost: document.querySelector(this._settings.installationCost),

            productPrice: document.querySelector(this._settings.productPrice),
            addToCartButton: document.querySelector(this._settings.addToCartButton),

            addToCartContainer: document.querySelector(this._settings.addToCartContainer),
            quantitySelector: document.querySelector(this._settings.quantitySelector),
            bisForm: document.querySelector(this._settings.bisForm),
            klarnaWidget: document.querySelector(this._settings.klarnaWidget)
        }

        /**
         * Global variables.
         */
        this._productDetails = {}
        this._installation = true
    }

    /**
   * Initialise component.
   */
    onInit () {
        this._setListeners()
        this._checkIfParent()
    }

    /**
     * Set event listeners.
     */
    _setListeners () {
        on('click', this._nodeSelectors.installationInput, (event) => this._handleInputClick(event))

        window.Frame.EventBus.listen('ProductKit:loaded', (response) => this._updateBundleParts(response))
        window.Frame.EventBus.listen('InstallationDetails:updated', () => this._handleDetailsUpdate())
    }

    /**
     * Check if parent and run code based on this.
     */
    _checkIfParent () {
        const isInstallationParent = this._nodeSelectors.installationDetails.getAttribute('data-has-installation')
        if (isInstallationParent === 'true') {
            this._productDetails.currentPage = 'parent'
            this._getChildProduct()
        } else {
            this._productDetails.currentPage = 'child'
            this._getParentProduct()
        }
    }

    /**
     * Parent product page functionality.
     * ------------------------------------------------------------------------------
     * Functionality run when landing on a parent product page.
     *
     */

    /**
     * Get bundle child sku.
     */
    _getChildProduct () {
        const childSku = this._nodeSelectors.installationDetails.getAttribute('data-search-string')
        const searchQuery = `/search?q=variants.sku:${childSku}&view=sku`

        axios.get(searchQuery)
            .then((response) => {
                if (
                    response &&
            typeof response.data !== 'string' &&
            response.data.products.length > 0
                ) {
                    return this._setProductDetails(window.ProductJSON, response.data.products[0])
                }

                throw new Error('No response found!')
            })
            .catch((error) => this._handleError(error))
    }

    /**
     * Child product page functionality.
     * ------------------------------------------------------------------------------
     * Functionality run when landing on a child product page.
     *
     */

    /**
     * Check if child of installation bundle.
     */
    _getParentProduct () {
        const childSku = this._nodeSelectors.installationDetails.getAttribute('data-sku')

        const searchQuery = `/search?q=tag:"Installation_Bundle: ${childSku}"&type=product&view=sku`
        axios.get(searchQuery)
            .then((response) => {
                if (
                    response &&
            typeof response.data !== 'string' &&
            response.data.products.length === 1
                ) {
                    return this._checkParentResponseForTag(response.data.products, childSku)
                }

                this._installation = false
                return null
            })
            .catch((error) => this._handleError(error))
    }

    /**
     * Check search results for sku tag.
     * @param {Object} response the response from the search.
     * @param {String} childProductSku the product sku.
     */
    _checkParentResponseForTag (response, childSku) {
        const parent = response.filter((product) => {
            return (product.tags.includes(`Installation_Bundle: ${childSku}`)) ? product : ''
        })[0]

        this._setProductDetails(parent, window.ProductJSON, 'child')
    }

    /**
     * General functionality.
     * ------------------------------------------------------------------------------
     * Functionality run when landing on either product pages.
     *
     */

    /**
     * Sets global object with details of both parent and child.
     * @param {Object} parent the parent product.
     * @param {Object} child the child product.
     */
    _setProductDetails (parent, child) {
        this._productDetails.child = {
            available: child.available,
            comparePrice: child.compare_at_price || null,
            id: child.variants[0].id,
            price: child.price,
            sku: child.variants[0].sku,
            title: child.title
        }

        this._productDetails.parent = {
            available: parent.available,
            comparePrice: parent.compare_at_price || null,
            id: parent.variants[0].id,
            price: parent.price,
            sku: parent.variants[0].sku,
            title: parent.title
        }

        this._productDetails.calculatedCost = this._getCalculatedCost()
        this._productDetails.fittingTime = this._getFittingTime(parent.tags)
        this._productDetails.parts = this._productDetails.parts || `${child.title} – (${child.variants[0].sku})`
        this._productDetails.productToAdd = this._productDetails.currentPage
        this._productDetails.tagCost = this._getTagCost(parent.tags)

        window.Frame.EventBus.emit('InstallationDetails:updated')
    }

    /**
     * Get the cost of installation from prices.
     */
    _getCalculatedCost () {
        return (this._productDetails.parent.price - this._productDetails.child.price)
    }

    /**
     * Get fitting time form tags.
     * @param {Array} tags the tags on the parent product.
     */
    _getFittingTime (tags) {
        const fittingTime = tags.find((item) => item.includes('Fitting_Time:'))

        if (!fittingTime) {
            return null
        }

        return fittingTime.replace('Fitting_Time: ', '')
    }

    /**
     * Get the cost of installation from tags.
     * Needs to support old `Installation_Cost: £12.34` format.
     * @param {Array} tags the tags on the parent product.
     */
    _getTagCost (tags) {
        let cost = tags.find((item) => item.includes('Installation_Cost:'))

        if (!cost) {
            return null
        }

        cost = cost.replace('Installation_Cost: ', '')

        if (cost.includes('.')) {
            let penceLength = cost.split('.')
            penceLength = penceLength[1].length

            cost = cost
                .replace('£', '')
                .replace('€', '')
                .replace('.', '')
                .replace(',', '')

            if (penceLength === 0) {
                cost += '00'
            } else if (penceLength === 1) {
                cost += '0'
            }
        }

        return formatMoney(cost, window.theme.moneyFormat)
    }

    /**
     * Handle an axios request error.
     * @param {String} error the axios error.
     */
    _handleError (error) {
        this._nodeSelectors.installationParent.classList.remove(cssClasses.active)
        this._nodeSelectors.installationError.classList.add(cssClasses.active)

        window.console.log('Axios Error', error)
    }

    /**
     * Labour cost checkbox click function
     * @param {Event} event the click event.
     */
    _handleInputClick (event) {
        const itemChecked = event.currentTarget.checked

        /**
         * Set product to add based on current page and checkbox.
         */
        if (this._productDetails.currentPage === 'parent') {
            if (itemChecked) {
                this._productDetails.productToAdd = 'parent'
            } else {
                this._productDetails.productToAdd = 'child'
            }
        } else if (this._productDetails.currentPage === 'child') {
            if (itemChecked) {
                this._productDetails.productToAdd = 'parent'
            } else {
                this._productDetails.productToAdd = 'child'
            }
        }

        window.Frame.EventBus.emit('InstallationDetails:updated')
    }

    /**
     * Handle the EventBus event when `productDetails` is updated.
     */
    _handleDetailsUpdate () {
        if (!this._installation) {
            return
        }

        this._renderPrices()
        this._checkAvailability()
        this._updateAddToCart()
    }

    /**
     * Work out install cost and update Klarna widget.
     */
    _renderPrices () {
        const installCost = formatMoney(this._productDetails.calculatedCost, window.theme.moneyFormat).replace('.00', '')

        this._nodeSelectors.installationCost.innerHTML = (window.theme.strings.products.includeInstall).replace('%cost%', installCost)
        this._nodeSelectors.productPrice.innerHTML = formatMoney(this._productDetails[this._productDetails.productToAdd].price, window.theme.moneyFormat)

        if (window.theme.klarnaMessaging) {
            this._nodeSelectors.klarnaWidget.setAttribute('data-purchase-amount', this._productDetails[this._productDetails.productToAdd].price)
            window.KlarnaOnsiteService = window.KlarnaOnsiteService || []
            window.KlarnaOnsiteService.push({eventName: 'refresh-placements'})
            if (this._productDetails[this._productDetails.productToAdd].price > 3500) {
                this._nodeSelectors.klarnaWidget.classList.add(cssClasses.active)
            } else {
                this._nodeSelectors.klarnaWidget.classList.remove(cssClasses.active)
            }
        }
    }

    /**
     * Check availability of products and update form accordingly.
     * @param {Boolean} available whether the product to add is available.
     */
    _checkAvailability () {
        this._showInstallation()
        this._showProductForm()

        if (this._productDetails.currentPage === 'child' && !this._productDetails.parent.available) {
            this._hideCheckbox()
        } else if (this._productDetails.currentPage === 'parent' && !this._productDetails.child.available) {
            this._disableInstallation()
        }

        if (!this._productDetails[this._productDetails.productToAdd].available) {
            this._hideProductForm()
        }
    }

    /**
     * Show entire installation element.
     */
    _showInstallation () {
        this._nodeSelectors.installationParent.classList.add(cssClasses.active)
    }

    /**
     * Disable interacting with checkbox.
     */
    _disableInstallation () {
        this._nodeSelectors.installationContainer.classList.add(cssClasses.disabled)
    }

    /**
     * Show the product form (quantity selector and add to cart).
     */
    _showProductForm () {
        this._nodeSelectors.addToCartContainer.classList.add(cssClasses.active)
        this._nodeSelectors.addToCartButton.removeAttribute('disabled')
        this._nodeSelectors.quantitySelector.classList.add(cssClasses.active)
        this._nodeSelectors.bisForm.classList.remove(cssClasses.active)

        this._nodeSelectors.installationParent.classList.add(cssClasses.active)
        this._nodeSelectors.installationSoldOut.classList.remove(cssClasses.active)
    }

    /**
     * Hide the product form (quantity selector and add to cart).
     */
    _hideProductForm () {
        this._nodeSelectors.addToCartContainer.classList.remove(cssClasses.active)
        this._nodeSelectors.addToCartButton.setAttribute('disabled', 'disabled')
        this._nodeSelectors.quantitySelector.classList.remove(cssClasses.active)
        this._nodeSelectors.bisForm.classList.add(cssClasses.active)
    }

    /**
     * Hide checkbox, show sold out error.
     */
    _hideCheckbox () {
        this._nodeSelectors.installationParent.classList.remove(cssClasses.active)
        this._nodeSelectors.installationSoldOut.classList.add(cssClasses.active)
    }

    /**
     * Set add to cart to add product based on `productDetails.add`.
     */
    _updateAddToCart () {
        const attributes = {
            'data-child-id': this._productDetails.child.id,
            'data-current-page': this._productDetails.currentPage,
            'data-installation-cost': this._productDetails.tagCost,
            'data-installation': (this._productDetails.productToAdd === 'parent'),
            'data-parent-id': this._productDetails.parent.id,
            'data-parts': this._productDetails.parts,
            'data-product-to-add': this._productDetails.productToAdd
        }

        if (this._productDetails.fittingTime) {
            attributes['data-fitting-time'] = this._productDetails.fittingTime
        }

        if (this._productDetails.child.comparePrice) {
            attributes['data-child-compare-price'] = this._productDetails.child.comparePrice
        }

        if (this._productDetails.parent.comparePrice) {
            attributes['data-parent-compare-price'] = this._productDetails.parent.comparePrice
        }

        Object.keys(attributes).forEach((key) => {
            this._nodeSelectors.addToCartButton.setAttribute(key, attributes[key])
        })
    }

    /**
     * Update `data-parts` after product kit has loaded.
     * @param {Object} products product kit products.
     */
    _updateBundleParts (products) {
        const parts = products.map((product) => {
            return `${product.title} – (${product.variants[0].sku})`
        }).join('/')

        this._productDetails.parts = parts

        window.Frame.EventBus.emit('InstallationDetails:updated')
    }

    /**
     * Expose public interface.
     */
    // return Object.freeze({
    //     init,
    // });
}

export default ProductInstallation



// WEBPACK FOOTER //
// ./src/resources/scripts/components/ProductInstallation.js